/**
 * Product Fit Bar Web Component
 * Extracts fit indicator data from Reviews.io widget and displays above product description
 */
class ProductFitBar extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.observer = null;
    this.initialized = false;
  }

  connectedCallback() {
    this.render();
    this.extractFitData();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  get headerText() {
    return this.getAttribute('header-text') || 'What our customers say:';
  }

  extractFitData() {
    const found = this.findAndExtractFit();
    if (!found) {
      this.observeForWidget();
    }
  }

  findAndExtractFit() {
    const widget = document.querySelector('#ReviewsWidget');
    if (!widget) return false;

    // Find the slider indicator for "Item Fit"
    const sliderIndicators = widget.querySelectorAll('.R-SliderIndicator');

    for (const slider of sliderIndicators) {
      const heading = slider.querySelector('.R-TextHeading');
      if (heading && heading.textContent.toLowerCase().includes('fit')) {
        const button = slider.querySelector('.R-SliderIndicator__button');
        if (button) {
          const leftStyle = button.style.left;
          if (leftStyle) {
            const percentage = parseFloat(leftStyle);
            if (!isNaN(percentage)) {
              this.updateFitIndicator(percentage);
              this.insertAboveDescription();
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  insertAboveDescription() {
    if (this.initialized) return;

    // Find the icons block and insert below it
    const iconsBlock = document.querySelector('.product-block.product-block__icons');

    if (iconsBlock) {
      this.initialized = true;  // Set BEFORE DOM manipulation to prevent recursion
      iconsBlock.insertAdjacentElement('afterend', this);
    }
  }

  observeForWidget() {
    this.observer = new MutationObserver(() => {
      const found = this.findAndExtractFit();
      if (found && this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Stop observing after 10 seconds to prevent memory leaks
    setTimeout(() => {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }, 10000);
  }

  updateFitIndicator(percentage) {
    const indicator = this.shadowRoot.querySelector('.fit-indicator');
    const container = this.shadowRoot.querySelector('.fit-bar-container');

    if (percentage === null || percentage === undefined || isNaN(percentage)) {
      this.hideFitBar();
      return;
    }

    percentage = Math.max(0, Math.min(100, percentage));
    container.style.display = 'block';
    indicator.style.left = `${percentage}%`;
  }

  hideFitBar() {
    const container = this.shadowRoot.querySelector('.fit-bar-container');
    if (container) {
      container.style.display = 'none';
    }
  }

  setFitValue(percentage) {
    this.updateFitIndicator(percentage);
  }

  refresh() {
    this.extractFitData();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          font-family: var(--fit-bar-font-family, inherit);
        }

        .fit-bar-container {
          display: none;
          padding: 15px 0;
          margin-bottom: 15px;
        }

        .fit-header {
          font-size: var(--fit-bar-header-size, 13px);
          font-weight: var(--fit-bar-header-weight, 600);
          color: var(--fit-bar-header-color, #000);
          text-transform: uppercase;
          margin-bottom: 16px;
          letter-spacing: 0.9px;
        }

        .fit-track-container {
          position: relative;
        }

        .fit-track {
          position: relative;
          height: 4px;
          background-color: var(--fit-bar-track-color, #c4c4c4);
        }

        .fit-indicator {
          position: absolute;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 4px;
          height: 12px;
          background-color: var(--fit-bar-indicator-color, #000);
          transition: left 0.3s ease;
        }

        .fit-labels-wrapper {
          position: relative;
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
        }

        .fit-labels-wrapper span {
          font-size: var(--fit-bar-scale-size, 11px);
          font-weight: var(--fit-bar-scale-weight, 300);
          color: var(--fit-bar-scale-color, #0E1311);
        }

        .fit-labels-wrapper .fit-label-center {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
        }
      </style>

      <div class="fit-bar-container">
        <div class="fit-header">${this.headerText}</div>
        <div class="fit-track-container">
          <div class="fit-track">
            <div class="fit-indicator"></div>
          </div>
        </div>
        <div class="fit-labels-wrapper">
          <span>Small</span>
          <span class="fit-label-center">True to Size</span>
          <span>Large</span>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('product-fit-bar')) {
  customElements.define('product-fit-bar', ProductFitBar);
}
